"use client"
import { useState, useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { getAuth, signOut } from "firebase/auth"
import { useNavigate } from "react-router-dom"
import { ref, onValue, auth, db } from "../Firebase/firebase-config"
import { getDatabase, update, remove } from "firebase/database"
import { onAuthStateChanged } from "firebase/auth"
import UserNotifications from "../Notification/UserNotifications"
import { Search, Bell, User, MenuIcon, X, HelpCircle, LogOut, Home, Clock, Tag } from "lucide-react"
import "./Navbar.css"
import { FaBuilding, FaChevronDown, FaChevronUp, FaFileInvoice, FaUsers } from "react-icons/fa"
import { useAccount } from "../context/AccountContext"
import { useCurrentUid } from "../hooks/useCurrentUid"
import Downloads from "../HomePage/Downloads"

const Navbar = () => {
  const navigate = useNavigate()
  const [companyName, setCompanyName] = useState(null)
  const [alarms, setAlarms] = useState([])
  const [newAlarms, setNewAlarms] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchVisible, setSearchVisible] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userUid, setUserUid] = useState("")
  const [showNotifications, setShowNotifications] = useState(false)
  const [unseenCount, setUnseenCount] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [hasNewOffers, setHasNewOffers] = useState(false)
  const { currentAccount, accounts, switchAccount, reload } = useAccount()
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState("")
  const notificationRef = useRef(null)
  const [showDownloadsDropdown, setShowDownloadsDropdown] = useState(false)
  const [selectedDownloadCategory, setSelectedDownloadCategory] = useState(null)
  const userDropdownRef = useRef(null)
  const downloadsRef = useRef(null)
  const currentUid = useCurrentUid()
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const location = useLocation();
  useEffect(() => {
    closeAllDropdowns();
  }, [location.pathname]);

  // Closes everything EXCEPT mobile menu
  const closeDesktopDropdowns = () => {
    setShowNotifications(false);
    setShowUserDropdown(false);
    setShowDownloadsDropdown(false);
    setShowAccountSwitcher(false);
  };

  // Closes EVERYTHING including mobile menu
  const closeAllDropdowns = () => {
    closeDesktopDropdowns();
    setMobileMenuOpen(false);
  };


  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (mobileMenuOpen && !event.target.closest('.website-mobile-toggle')) {
        setMobileMenuOpen(false);
      }

      // Close account switcher if click outside
      if (showAccountSwitcher && !event.target.closest('.account-switcher-panel')) {
        setShowAccountSwitcher(false);
      }

    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // Update company name from current account
  useEffect(() => {
    if (currentAccount?.company_name) {
      setCompanyName(currentAccount.company_name);
    }
  }, [currentAccount]);

  // Delete Firm
  const handleDeleteFirm = async (uid) => {
    if (!window.confirm("Delete this firm permanently? This cannot be undone.")) return;
    try {
      await remove(ref(db, `profileDashboard/${uid}`));
      await reload();
      if (currentAccount?.uid === uid) {
        switchAccount(getAuth().currentUser.uid);
      }
    } catch (err) {
      alert("Failed to delete firm");
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true);
        setUserUid(user.uid);
      } else {
        setIsAuthenticated(false);
        setUserUid("");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSearchToggle = () => {
    setSearchVisible(!searchVisible);
  };

  useEffect(() => {
    if (currentUid) {
      setUnseenCount(0);
      fetchUnseenNotifications(currentUid);
    }
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) return;

    const offersRef = ref(db, "offers");
    const viewedRef = ref(db, `user_viewed_offers/${currentUid}`);

    let offersData = {};
    let viewedData = {};

    const updateHasNew = () => {
      let hasNew = false;
      Object.keys(offersData).forEach(brand => {
        Object.keys(offersData[brand]).forEach(offerId => {
          const key = `${brand}-${offerId}`;
          if (!viewedData[key]) {
            hasNew = true;
          }
        });
      });
      setHasNewOffers(hasNew);
    };

    const unsubOffers = onValue(offersRef, (snapshot) => {
      offersData = snapshot.val() || {};
      updateHasNew();
    });

    const unsubViewed = onValue(viewedRef, (snapshot) => {
      viewedData = snapshot.val() || {};
      updateHasNew();
    });

    return () => {
      unsubOffers();
      unsubViewed();
    };
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) return;

    const db = getDatabase();
    const alarmsRef = ref(db, `alt_alarm/${currentUid}`);

    const unsubscribe = onValue(alarmsRef, (snapshot) => {
      if (snapshot.exists()) {
        const alarmsData = snapshot.val();
        const alarmList = Object.entries(alarmsData).map(([alarmId, alarm]) => ({
          alarmId,
          ...alarm,
        }));
        setAlarms(alarmList);

        const hasNew = alarmList.some((alarm) => alarm.isNew);
        setNewAlarms(hasNew);
      } else {
        setAlarms([]);
        setNewAlarms(false);
      }
    });

    return () => unsubscribe();
  }, [currentUid]);

  const handleAlarmClick = async () => {
    if (!currentUid) return;

    const db = getDatabase();
    const alarmsRef = ref(db, `alt_alarm/${currentUid}`);

    const updates = {};
    alarms.forEach((alarm) => {
      updates[`${alarm.alarmId}/isNew`] = false;
    });

    await update(alarmsRef, updates);
    setNewAlarms(false);
  };







  // searchbar functions
  const pages = [
    { name: "Cement Order", path: "/dashboard/product-order" },
    { name: "Ultratech Cement", path: "/dashboard/product-order" },
    { name: "Shree Cement", path: "/dashboard/product-order" },
    { name: "JSW Cement", path: "/dashboard/product-order" },
    { name: "JK Super Cement", path: "/dashboard/product-order" },
    { name: "ACC Cement", path: "/dashboard/product-order" },
    { name: "Ambuja Cement", path: "/dashboard/product-order" },
    { name: "Pending Order", path: "/dashboard/order-status" },
    { name: "UnderLoading Order", path: "/dashboard/order-status" },
    { name: "Dispatch Order", path: "/dashboard/order-status" },
    { name: "Paused Order", path: "/dashboard/order-status" },
    { name: "Continued Order", path: "/dashboard/order-status" },
    { name: "Completed Order", path: "/dashboard/order-status" },
    { name: "Realtime Tracking", path: "/dashboard/order-status" },
    { name: "Add Payment", path: "/dashboard/order-status" },
    { name: "QuoteRequest", path: "/dashboard/quote-request" },
    { name: "My Orders", path: "/dashboard/order-status" },
    { name: "Order Status", path: "/dashboard/order-status" },
    { name: "Alert", path: "/dashboard/alt-alarm" },
    { name: "Offers", path: "/dashboard/newoffers" },
    { name: "Query", path: "/dashboard/new-query" },
    { name: "NewProduct Offer", path: "/dashboard/newSupplier-products" },
    { name: "Finance", path: "/dashboard/finance" },
    { name: "Loan", path: "/dashboard/finance" },
    { name: "Loan", path: "/dashboard/finance" },
    { name: "Contact Support", path: "/dashboard/contact-us" },
    { name: "Loan History", path: "/dashboard/loan-history" },
    { name: "Alarm", path: "/dashboard/alt-alarm" },
    { name: "Previous Orders", path: "/dashboard/previous-orders" },
    { name: "Steel Order", path: "/dashboard/product-order" },
    { name: "Machinery Order", path: "/dashboard/product-order" },
    { name: "Purchase Order (PO)", path: "/dashboard/PO" },
    { name: "New Code", path: "/dashboard/documents" },
    { name: "Documents", path: "/dashboard/documents" },
    { name: "Stock Status", path: "/dashboard/stock-status" },
    { name: "RateHistory", path: "/dashboard/rate-history" },
    { name: "Transport Goods", path: "/dashboard/transport-goods" },
  ]

  const filteredPages = pages
    .filter((page) => page.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(
      (page, index, self) =>
        index === self.findIndex((p) => p.path === page.path)
    );


  // Handle search and navigate to the selected page
  const handleSearchSelect = (path) => {
    navigate(path)
    setSearchQuery("")
    setMobileMenuOpen(false)
  }

 const handleLogoutClick = () => {
  setShowLogoutModal(true)
}

const handleLogoutYes = () => {
  const auth = getAuth()

  signOut(auth)
    .then(() => {
      setError("")
      setShowLogoutModal(false)
      navigate("/login")
    })
    .catch((error) => {
      console.error("Error during sign out:", error)
      setShowLogoutModal(false)
    })
}

const handleLogoutNo = () => {
  setShowLogoutModal(false)
}
  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      if (filteredPages.length > 0) {
        handleSearchSelect(filteredPages[0].path)
      } else {
        alert("No matching results found")
      }
    }
  }

  useEffect(() => {
    // Get current user ID
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUnseenNotifications(currentUid);
      }
    })
    return () => unsubscribe()
  }, [currentUid])
  useEffect(() => {
    if (!currentUid) return;

    const offersRef = ref(db, "offers");
    const viewedRef = ref(db, `user_viewed_offers/${currentUid}`);

    let offersData = {};
    let viewedData = {};

    const updateHasNew = () => {
      let hasNew = false;
      Object.keys(offersData).forEach(brand => {
        Object.keys(offersData[brand]).forEach(offerId => {
          const key = `${brand}-${offerId}`;
          if (!viewedData[key]) {
            hasNew = true;
          }
        });
      });
      setHasNewOffers(hasNew);
    };

    const unsubOffers = onValue(offersRef, (snapshot) => {
      offersData = snapshot.val() || {};
      updateHasNew();
    });

    const unsubViewed = onValue(viewedRef, (snapshot) => {
      viewedData = snapshot.val() || {};
      updateHasNew();
    });

    return () => {
      unsubOffers();
      unsubViewed();
    };
  }, [currentUid, db]);
  // Fix the notification count issue in fetchUnseenNotifications function
  const fetchUnseenNotifications = (uid) => {
    const db = getDatabase()

    const notificationTypes = [
      "Order Dispatch",
      "Query Response",
      "Negotiation Response",
      "Quotation Response",
      "Raised Issues",
      "transportGoods",
      "Steel Orders",
      "machinery orders",
      "Low Balance",
      "Rate Response",
      "Refund Added",
      "Missing_Docs",
      "PO_Alerts",
      "New Code",
      "Partner-Management",
      "StockStatus"
    ]

    // Reset unseen count before fetching
    setUnseenCount(0)

    // Create a counter to track total unseen notifications
    let totalUnseen = 0

    // Fetch unseen notifications from standard nodes
    notificationTypes.forEach((type) => {
      const notificationsRef = ref(db, `user_notifications/${type}/${uid}`)

      onValue(notificationsRef, (snapshot) => {
        if (snapshot.exists()) {
          const notifications = snapshot.val()
          const unseenCount = Object.values(notifications).filter((notif) => !notif.seen && !notif.clicked).length

          totalUnseen += unseenCount
          setUnseenCount(totalUnseen)

          // Auto-show notifications when new ones arrive
          if (unseenCount > 0) {
            setShowNotifications(true)
          }
        }
      })
    })

    // Fetch unseen NewProduct notifications (stored under multiple supplierIds)
    const newProductRef = ref(db, `user_notifications/NewProduct`)

    onValue(newProductRef, (snapshot) => {
      if (snapshot.exists()) {
        const newProductData = snapshot.val()
        let unseenNewProductCount = 0

        Object.entries(newProductData).forEach(([supplierId, supplierNotifications]) => {
          Object.values(supplierNotifications).forEach((notif) => {
            if (!notif.seen && !notif.clicked) {
              unseenNewProductCount += 1
            }
          })
        })

        totalUnseen += unseenNewProductCount
        setUnseenCount(totalUnseen)

        // Auto-show notifications when new ones arrive
        if (unseenNewProductCount > 0) {
          setShowNotifications(true)
        }
      }
    })

    // Fetch unseen BrandRates notifications
    const brandRatesRef = ref(db, `user_notifications/BrandRates`)

    onValue(brandRatesRef, (snapshot) => {
      if (snapshot.exists()) {
        const brandRatesData = snapshot.val()
        let unseenBrandRatesCount = 0

        Object.entries(brandRatesData).forEach(([supplierId, supplierNotifications]) => {
          Object.values(supplierNotifications).forEach((notif) => {
            if (!notif.seen && !notif.clicked) {
              unseenBrandRatesCount += 1
            }
          })
        })

        totalUnseen += unseenBrandRatesCount
        setUnseenCount(totalUnseen)

        // Auto-show notifications when new ones arrive
        if (unseenBrandRatesCount > 0) {
          setShowNotifications(true)
        }
      }
    })
  }

  const markNotificationsAsSeen = () => {
    if (currentUid) {
      const db = getDatabase()

      const notificationTypes = [
        "Order Dispatch",
        "Query Response",
        "Negotiation Response",
        "Quotation Response",
        "Raised Issues",
        "transportGoods",
        "Steel Orders",
        "machinery orders",
        "Low Balance",
        "Rate Response",
        "Refund Added",
        "Missing_Docs",
        "PO_Alerts",
        "New Code",
        "Partner-Management",
        "StockStatus"
      ]

      // Mark standard notifications as seen
      notificationTypes.forEach((type) => {
        const notificationsRef = ref(db, `user_notifications/${type}/${currentUid}`)

        onValue(notificationsRef, (snapshot) => {
          if (snapshot.exists()) {
            const notifications = snapshot.val()
            Object.keys(notifications).forEach((key) => {
              if (!notifications[key].seen) {
                const notificationRef = ref(db, `user_notifications/${type}/${currentUid}/${key}`)
                update(notificationRef, { seen: true })
              }
            })
          }
        })
      })

      // Mark NewProduct notifications as seen (multiple supplier IDs)
      const productRef = ref(db, `user_notifications/NewProduct`)

      onValue(productRef, (snapshot) => {
        if (snapshot.exists()) {
          const brandRatesData = snapshot.val()

          Object.entries(brandRatesData).forEach(([supplierId, supplierNotifications]) => {
            Object.keys(supplierNotifications).forEach((key) => {
              if (!supplierNotifications[key].seen) {
                const notificationRef = ref(db, `user_notifications/NewProduct/${supplierId}/${key}`)
                update(notificationRef, { seen: true })
              }
            })
          })
        }
      })

      // Mark BrandRates notifications as seen (multiple supplier IDs)
      const brandRatesRef = ref(db, `user_notifications/BrandRates`)

      onValue(brandRatesRef, (snapshot) => {
        if (snapshot.exists()) {
          const brandRatesData = snapshot.val()

          Object.entries(brandRatesData).forEach(([supplierId, supplierNotifications]) => {
            Object.keys(supplierNotifications).forEach((key) => {
              if (!supplierNotifications[key].seen) {
                const notificationRef = ref(db, `user_notifications/BrandRates/${supplierId}/${key}`)
                update(notificationRef, { seen: true })
              }
            })
          })
        }
      })
    }
  }

  useEffect(() => {
    if (currentUid) {
      fetchUnseenNotifications(currentUid)
    }
  }, [currentUid])

  useEffect(() => {
    setCompanyName(currentAccount?.company_name || "AltRange");
  }, [currentAccount]);

  useEffect(() => {
    if (!currentUid) return;

    const db = getDatabase();
    const alarmsRef = ref(db, `alt_alarm/${currentUid}`);

    const unsubscribe = onValue(alarmsRef, (snapshot) => {
      if (snapshot.exists()) {
        const alarmsData = snapshot.val();
        const alarmList = Object.entries(alarmsData).map(([alarmId, alarm]) => ({
          alarmId,
          ...alarm,
        }));
        setAlarms(alarmList);

        const hasNew = alarmList.some((alarm) => alarm.isNew);
        setNewAlarms(hasNew);
      } else {
        setAlarms([]);
        setNewAlarms(false);
      }
    });

    return () => unsubscribe();
  }, [currentUid]);


  const handleNavLinkClick = (path) => {
    navigate(path);
    closeAllDropdowns(); // mobile menu MUST close here
  };


  const toggleMobileMenu = () => {
    closeAllDropdowns(); // Close all others
    setMobileMenuOpen(prev => !prev); // Toggle mobile menu
  };

  const handleUserIconClick = () => {
    closeAllDropdowns(); // fully close everything
    setShowUserDropdown(prev => !prev);
  };


  const handleNotificationClick = () => {
    closeAllDropdowns(); // Close all others
    if (unseenCount > 0) {
      markNotificationsAsSeen()
      setUnseenCount(0)
    }
    setShowNotifications(!showNotifications)
  };


  return (
    <div className="website-navbar-wrapper">
      <nav className="website-navbar">
        <div className="website-navbar-container">
          {/* Brand Logo */}
          <div className="website-brand" onClick={() => navigate("/dashboard")}>
            <Home className="brand-icons" />
            <span className="brand-text">Altrange</span>
          </div>

          {/* Desktop Nav Links */}
          <div className="website-nav-links">
            <Link to="/dashboard/newSupplier-products" className="website-nav-link">Products</Link>
            <Link to="/dashboard/transport-goods" className="website-nav-link">Transport</Link>
            <div className="website-nav-link" ref={downloadsRef} style={{ position: "relative", cursor: "pointer" }} onClick={() => setShowDownloadsDropdown(!showDownloadsDropdown)}>
              Downloads
              {showDownloadsDropdown && (
                <div className="website-nav-dropdown" style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "8px", width: "180px", zIndex: 1000 }}>
                  <button className="dropdown-option" onClick={() => { setSelectedDownloadCategory("mtc"); setShowDownloadsDropdown(false); }}>
                    <FaFileInvoice style={{ marginRight: "8px" }} /> MTC
                  </button>
                  <button className="dropdown-option" onClick={() => { setSelectedDownloadCategory("statement"); setShowDownloadsDropdown(false); }}>
                    <FaFileInvoice style={{ marginRight: "8px" }} /> Statement
                  </button>
                </div>
              )}
            </div>
            <Link to="/dashboard/newoffers" className="website-nav-link">
              Offers {hasNewOffers && <span className="nav-badge">New</span>}
            </Link>
            <Link to="/dashboard/finance" className="website-nav-link">Finance</Link>
            <Link to="/dashboard/contact-us" className="website-nav-link">Support</Link>
          </div>

          {/* Search Bar */}
          <div className="website-search-container">
            <div className="website-search-wrapper">
              <Search className="website-search-icon" />
              <input
                className="website-search-input"
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            {searchQuery && (
              <ul className="website-search-results">
                {filteredPages.length > 0 ? (
                  filteredPages.map((page) => (
                    <li
                      key={page.path}
                      className="website-search-result-item"
                      onClick={() => handleSearchSelect(page.path)}
                    >
                      {page.name}
                    </li>
                  ))
                ) : (
                  <li className="website-search-result-item not-found">No results found</li>
                )}
              </ul>
            )}
          </div>

          {/* Right Actions */}
          <div className="website-navbar-actions">
            {/* Notifications */}
            <div className="website-action-item" ref={notificationRef}>
              <button className="website-icon-btn with-label" onClick={handleNotificationClick}>
                <Bell className="action-icon" />
                <span className="action-label">Notifications</span>
                {unseenCount > 0 && <span className="website-badge">{unseenCount > 9 ? "9+" : unseenCount}</span>}
              </button>
              {showNotifications && (
                <div className="website-notification-dropdown">
                  <div className="website-notification-header">
                    {/* <h3>Notifications</h3> */}
                    {unseenCount > 0 && <span className="website-count-badge">{unseenCount} new</span>}
                  </div>
                  <div className="website-notification-content">
                    <UserNotifications userUid={currentUid} />
                  </div>
                </div>
              )}
            </div>

            {/* Alarm */}
            <Link to="/dashboard/alt-alarm" className="website-action-item" onClick={handleAlarmClick}>
              <button className="website-icon-btn with-label">
                <Clock className="action-icon" />
                <span className="action-label">Alarm</span>
                {newAlarms && <span className="website-badge-dot"></span>}
              </button>
            </Link>

            {/* User Dropdown */}
            <div className="website-action-item" ref={userDropdownRef}>
              <button className="website-icon-btn with-label" onClick={handleUserIconClick}>
                <User className="action-icon" />
                <span className="action-label">Account</span>
              </button>
              {showUserDropdown && (
                <div className="website-user-dropdown">
                  <div className="website-user-header">
                    <p className="website-user-greeting">Hello, {companyName || "User"}!</p>
                  </div>

                  <Link to="/profile" className="website-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <Home size={18} />
                    <span>Profile</span>
                  </Link>

                  {currentAccount?.type === "main" && (
                    <Link to="/dashboard/partner-management" className="website-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                      <FaUsers size={18} />
                      <span>Partner Management</span>
                    </Link>
                  )}

                  <button
                    className="website-dropdown-item"
                    onClick={() => {
                      setShowAccountSwitcher(true)
                      setShowUserDropdown(false)
                    }}
                  >
                    <FaBuilding size={18} />
                    <span>Switch Account</span>
                  </button>

                  {currentAccount?.type === "main" && (
                    <Link to="/add-firms" className="website-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                      <FaBuilding size={18} />
                      <span>Add Firms</span>
                    </Link>
                  )}

                  <Link to="/dashboard/need-help" className="website-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <HelpCircle size={18} />
                    <span>Help</span>
                  </Link>

                  <button className="website-dropdown-item logout-item" onClick={handleLogoutClick}>
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
            {showLogoutModal && (
  <div className="logout-modal-overlay">
    <div className="logout-modal-box">
      <h3 className="logout-modal-title">Confirm Logout</h3>
      <p className="logout-modal-text">
        Are you sure you want to logout?
      </p>

      <div className="logout-modal-actions">
        <button
          className="logout-modal-btn logout-modal-no"
          onClick={handleLogoutNo}
        >
          No
        </button>

        <button
          className="logout-modal-btn logout-modal-yes"
          onClick={handleLogoutYes}
        >
          Yes
        </button>
      </div>
    </div>
  </div>
)}


            {/* Mobile Toggle */}
            <button className="website-mobile-toggle" onClick={() => {
              closeDesktopDropdowns();   // close others
              setMobileMenuOpen(prev => !prev); // toggle menu
            }}>
              {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="website-mobile-menu">
            <div className="website-mobile-search-wrapper" style={{ position: "relative" }}>
              <div className="website-mobile-search">
                <Search className="website-search-icon mt-3" />
                <input
                  className="website-search-input"
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              {/* Add search results HERE in mobile */}
              {searchQuery && (
                <ul className="website-search-results mobile-search-results">
                  {filteredPages.length > 0 ? (
                    filteredPages.map((page) => (
                      <li
                        key={page.path}
                        className="website-search-result-item"
                        onClick={() => handleSearchSelect(page.path)}
                      >
                        {page.name}
                      </li>
                    ))
                  ) : (
                    <li className="website-search-result-item not-found">
                      No results found
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="website-mobile-links">
              <Link to="/dashboard/newSupplier-products" className="website-mobile-link" onClick={() => setMobileMenuOpen(false)}>Products</Link>
              <Link to="/dashboard/transport-goods" className="website-mobile-link" onClick={() => setMobileMenuOpen(false)}>Transport</Link>

              {/* DOWNLOADS in mobile - CORRECTED */}
              <div className="website-mobile-link" ref={downloadsRef} style={{ position: "relative", cursor: "pointer" }} onClick={() => setShowDownloadsDropdown(!showDownloadsDropdown)}>
                Downloads
                <span>{showDownloadsDropdown ? <FaChevronUp /> : <FaChevronDown />}</span>
                {showDownloadsDropdown && (
                  <div className="website-nav-dropdown" style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "8px", width: "100%", zIndex: 1000 }}>
                    <button className="dropdown-option" onClick={() => { setSelectedDownloadCategory("mtc"); setShowDownloadsDropdown(false); }}>
                      <FaFileInvoice style={{ marginRight: "8px" }} /> MTC
                    </button>
                    <button className="dropdown-option" onClick={() => { setSelectedDownloadCategory("statement"); setShowDownloadsDropdown(false); }}>
                      <FaFileInvoice style={{ marginRight: "8px" }} /> My Statement
                    </button>
                  </div>
                )}
              </div>
              <Link to="/dashboard/newoffers" className="website-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                Offers {hasNewOffers && <span className="nav-badge">New</span>}
              </Link>
              <Link to="/dashboard/finance" className="website-mobile-link" onClick={() => setMobileMenuOpen(false)}>Finance</Link>
              <Link to="/dashboard/contact-us" className="website-mobile-link" onClick={() => setMobileMenuOpen(false)}>Support</Link>




            </div>
          </div>
        )}
      </nav>
      {/* Downloads Full Modal */}
      {selectedDownloadCategory && (
        <>
          {/* Add class to body to prevent scrolling */}
          {document.body.classList.add("modal-open")}

          <div
            className="downloads-modal-overlay"
            onClick={() => {
              setSelectedDownloadCategory(null)
              document.body.classList.remove("modal-open")
            }}
          >
            <div
              className="downloads-modal"
              onClick={(e) => e.stopPropagation()} // Crucial: prevents closing when clicking inside
            >
              {/* Close Button */}
              <button
                className="close-modal"
                onClick={() => {
                  setSelectedDownloadCategory(null)
                  document.body.classList.remove("modal-open")
                }}
              >
                ×
              </button>

              {/* Your Downloads Component */}
              <Downloads
                category={selectedDownloadCategory}
                onClose={() => {
                  setSelectedDownloadCategory(null)
                  document.body.classList.remove("modal-open")
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Account Switcher Modal */}
      {showAccountSwitcher && (
        <div className="account-switcher-overlay" onClick={() => setShowAccountSwitcher(false)}>
          <div className="account-switcher-panel" onClick={(e) => e.stopPropagation()}>
            <h3 className="switcher-title">Switch Account</h3>
            {accounts.map((acc) => (
              <div
                key={acc.uid}
                className={`switcher-account-item ${currentAccount?.uid === acc.uid ? "active" : ""}`}
                onClick={async () => {
                  if (currentAccount?.uid !== acc.uid) {
                    setSwitching(true)
                    await switchAccount(acc.uid)
                    setSwitching(false)
                    setShowAccountSwitcher(false)
                  }
                }}
              >
                <div className="switcher-avatar">{acc.company_name?.charAt(0) || "A"}</div>
                <div className="switcher-info">
                  <div className="switcher-name">{acc.company_name || "Unnamed Firm"}</div>
                  {currentAccount?.uid === acc.uid && <div className="switcher-current">Current</div>}
                </div>
              </div>
            ))}
            {currentAccount?.type === "main" && (
              <Link to="/add-firms" className="switcher-add-firm" onClick={() => setShowAccountSwitcher(false)}>
                + Add New Firm
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Switching Loader */}
      {switching && (
        <div className="switch-loader-overlay">
          <div className="switch-loader"></div>
        </div>
      )}
    </div>
  )
}

export default Navbar